// SF3 — Competitor Price Comparator, and SF5 — Category Price Positioning.
//
// Both are market-facing and both are waiting on the same missing capture, so
// they share a panel rather than presenting as two empty tables.
//
// SF3 is declared by the PRD as THE SINGLE SOURCE of Buy Box state for the whole
// platform — no other feature detects it independently. That makes its gaps
// everyone's gaps: SF1 marks no day as Buy Box confounded and SF4 applies no
// ceiling because of what is missing here, so the missing pieces are named
// individually rather than hidden behind one "no data" line.
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { FiAlertTriangle, FiInfo } from 'react-icons/fi';

import AuthContext from '../../../../context/AuthContext';
import local from '../Pricing.module.css';
import { getCompetitors, getPositioning } from '../services/pricingApi';

const MarketPosition = () => {
  const authCtx = useContext(AuthContext);
  const token = authCtx?.token;
  const [data, setData] = useState(null);
  const [positioning, setPositioning] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true); setError(null);
    try {
      const [competitors, position] = await Promise.all([
        getCompetitors(token), getPositioning(token),
      ]);
      setData(competitors);
      setPositioning(position);
    } catch (err) { setError(err); setData(null); }
    finally { setLoading(false); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const products = data?.buy_box?.products || [];

  return (
    <section className={local.card}>
      <div className={local.cardHead}>
        <h2 className={local.cardTitle}>Where you stand on your own listings</h2>
        {data?.buy_box?.measured > 0 && (
          <span className={local.muted}>
            {data.buy_box.mostly_losing} of {data.buy_box.measured} mostly losing the Buy Box
          </span>
        )}
      </div>

      {error && (
        <div className={`${local.banner} ${local.bannerError}`}>
          <FiAlertTriangle className={local.bannerIcon} />
          <div><strong>Could not load market position</strong><div>{error.message}</div></div>
        </div>
      )}

      {!error && products.length > 0 && (
        <div className={local.tableScroll}>
          <table className={local.table}>
            <thead>
              <tr>
                <th>Product</th>
                <th className={local.numeric}>Buy Box share</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {products.map((row) => (
                <tr key={row.asin}>
                  <td>{row.asin}</td>
                  <td className={local.numeric}>
                    <span className={row.mostly_losing ? local.loss : local.context}>
                      {row.buy_box_share}%
                    </span>
                  </td>
                  <td className={local.context}>
                    {row.mostly_losing ? 'Mostly not the featured offer' : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!error && loading && products.length === 0 && (
        <div className={local.emptyCell}>Loading…</div>
      )}

      {/* Named individually, because each gap disables something specific
          elsewhere on the platform. */}
      {data?.unavailable && (
        <div className={local.coverage}>
          <strong>Not captured yet</strong>
          <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
            {Object.entries(data.unavailable).map(([key, why]) => (
              <li key={key}>{why}</li>
            ))}
            {positioning && !positioning.available && (
              <li>{positioning.reason}</li>
            )}
          </ul>
        </div>
      )}

      {data?.notes?.length > 0 && (
        <ul className={local.noteList}>
          {data.notes.slice(0, 4).map((note) => (
            <li key={note}><FiInfo className={local.noteIcon} />{note}</li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default MarketPosition;
